import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from 'react';
import Highcharts from 'highcharts';
import Chart from 'highcharts-react-official';
import { debounce } from 'lodash-es';
import { ExportImageButton } from './export-image-button';
import { ExportPushDrawer } from './export-push-drawer';
import { useChartExportSection } from './chart-export-section';
import { createHighchartsExporter } from './highcharts-exporter';
import { GENERIC_CHART_ELEMENTS } from './chart-element-presets';
import type {
  ExportLegendItem,
  HighchartsElementDescriptor,
  HighchartsExporterFeatures,
  OutputFormat,
} from './types';
import styles from './exportable-highchart.module.scss';

// `highcharts-react-official`'s ref shape (a `{ chart }` box, not the chart).
type HighchartsReactRefObject = { chart: Highcharts.Chart };

type ChartProps = ComponentProps<typeof Chart>;

// Opt-in image-export config — mirrors SciChartView's `exportConfig`. Omit to
// render a plain chart with no export trigger.
export interface HighchartsExportConfig {
  label: string;
  filenamePrefix: string;
  // Drawer toggle list. Defaults to the cartesian set; pass PIE_CHART_ELEMENTS
  // for pies.
  elements?: HighchartsElementDescriptor[];
  // Pass CATEGORY_AXIS_FEATURES for column / bar / category-axis charts.
  features?: HighchartsExporterFeatures;
  filterSeries?: (series: Highcharts.SeriesOptionsType) => boolean;
  // Omit (PNG-only) or pass ['png','jpeg'] to surface the format radios.
  supportedFormats?: OutputFormat[];
  // X-axis title to show in the EXPORT only — for charts whose x-axis label is an
  // external switcher/dropdown rather than an on-axis title.
  xAxisTitle?: string;
  // Export-only legend items — for charts whose on-screen legend is a React
  // component (not a Highcharts legend) the SVG export can't capture.
  legendItems?: ExportLegendItem[];
  // Class on the download-icon button — e.g. to override its fill color.
  iconClassName?: string;
}

interface ExportableHighchartProps extends Omit<
  ChartProps,
  'ref' | 'highcharts'
> {
  // Supply to render the icon-only export trigger + in-section push drawer;
  // omit for a plain chart.
  exportConfig?: HighchartsExportConfig;
  // Charts using the chart imperatively (showLoading, …) pass their own ref;
  // pure charts omit it and an internal ref is used.
  chartRef?: RefObject<HighchartsReactRefObject | null>;
  // Extra class on the flex row root.
  rootClassName?: string;
  // Content (e.g. filter controls) shown in the header row, left of the export
  // trigger. Omit for a header row with just the trigger.
  header?: ReactNode;
  // Overlay the export trigger top-right inside the chart's title band (like
  // SciChartView) instead of a separate row above it. For charts with a title band.
  overlayTrigger?: boolean;
  // Defaults to the app's Highcharts; overridable for custom constructors.
  highcharts?: ChartProps['highcharts'];
}

// Shared Highcharts host with opt-in, self-contained image export (icon trigger +
// local-state, per-chart push drawer). Drop-in for <Chart>; never touches globals.
export const ExportableHighchart = ({
  exportConfig,
  chartRef,
  rootClassName,
  header,
  overlayTrigger = false,
  highcharts = Highcharts,
  ...chartProps
}: ExportableHighchartProps) => {
  const internalRef = useRef<HighchartsReactRefObject | null>(null);
  const ref = chartRef ?? internalRef;
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [exportOpen, setExportOpen] = useState(false);
  // When inside a ChartExportSection, the section owns one shared push drawer
  // (compresses the whole group). Otherwise this chart hosts its own.
  const openInSection = useChartExportSection();

  const getChart = useCallback(() => ref.current?.chart ?? null, [ref]);

  const elements = exportConfig?.elements ?? GENERIC_CHART_ELEMENTS;
  const features = exportConfig?.features;
  const filterSeries = exportConfig?.filterSeries;
  const xAxisTitle = exportConfig?.xAxisTitle;
  const legendItems = exportConfig?.legendItems;

  const exporter = useMemo(
    () =>
      createHighchartsExporter({
        getChart,
        elementHooks: { getElements: () => elements },
        filterSeries,
        features,
        xAxisTitle,
        legendItems,
      }),
    [getChart, elements, features, filterSeries, xAxisTitle, legendItems]
  );

  // highcharts-react-official doesn't observe container size, so reflow the
  // chart when the push drawer opens/closes and its area resizes.
  useEffect(() => {
    const el = chartAreaRef.current;
    if (!el) return;
    // React 18 can fire a queued reflow after HighchartsReact has destroyed
    // the chart; touching a destroyed chart throws (reads this.legend of
    // undefined). Bail if the chart is gone (renderer nulled on destroy).
    const reflow = debounce(() => {
      const chart = ref.current?.chart;
      if (!chart?.renderer) return;
      chart.reflow();
    }, 100);
    const observer = new ResizeObserver(() => reflow());
    observer.observe(el);
    return () => {
      observer.disconnect();
      reflow.cancel();
    };
  }, [ref]);

  // Memoized: highcharts-react runs a full chart.update() whenever options
  // identity changes, so an inline object here would update on every re-render (INS-10926).
  const options = chartProps.options;
  const mergedOptions = useMemo(
    () => ({
      ...options,
      // Hide the native Highcharts export menu (hamburger); this host renders
      // its own download trigger. A consumer's `exporting` settings still win.
      exporting: { enabled: false, ...options?.exporting },
    }),
    [options]
  );

  const chart = (
    <Chart
      ref={ref}
      highcharts={highcharts}
      {...chartProps}
      options={mergedOptions}
    />
  );

  if (!exportConfig) return chart;

  const trigger = (
    <ExportImageButton
      exporter={exporter}
      chartLabel={exportConfig.label}
      filenamePrefix={exportConfig.filenamePrefix}
      supportedFormats={exportConfig.supportedFormats}
      iconOnly
      // `header` anchors top-right inside the chart root (over its title band);
      // `inline` sits in a header row above the plot.
      placement={overlayTrigger ? 'header' : 'inline'}
      className={exportConfig.iconClassName}
      onOpen={() => {
        if (openInSection) {
          openInSection({
            exporter,
            chartLabel: exportConfig.label,
            filenamePrefix: exportConfig.filenamePrefix,
            supportedFormats: exportConfig.supportedFormats,
          });
        } else {
          setExportOpen(true);
        }
      }}
    />
  );

  // Default: header row above the chart (consumer content left, trigger right).
  // `overlayTrigger`: trigger sits in the chart root; row shown only for `header`.
  const chartWithHeader = (
    <>
      {(header || !overlayTrigger) && (
        <div className={styles.headerRow}>
          {header && <div className={styles.headerContent}>{header}</div>}
          {!overlayTrigger && trigger}
        </div>
      )}
      <div className={styles.chartRoot} ref={chartAreaRef}>
        {chart}
        {overlayTrigger && trigger}
      </div>
    </>
  );

  // In a section: the section owns the shared push drawer + flex row that
  // compresses the whole group, so render just this chart + its header here.
  if (openInSection) {
    return <div className={rootClassName}>{chartWithHeader}</div>;
  }

  // Standalone: self-contained push drawer beside this single chart.
  return (
    <ExportPushDrawer
      payload={
        exportOpen
          ? {
              exporter,
              chartLabel: exportConfig.label,
              filenamePrefix: exportConfig.filenamePrefix,
              supportedFormats: exportConfig.supportedFormats,
            }
          : null
      }
      onClose={() => setExportOpen(false)}
      className={rootClassName}
    >
      {chartWithHeader}
    </ExportPushDrawer>
  );
};
